import React, {FC, forwardRef, ReactNode, useEffect, useRef} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/components/trans';
import {useForwardRef} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {
  OnboardingEventsEnum,
  trackEvent,
  TrackingTypesEnum,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import PlayButton from '../../../assets/images/playButton.svg';
import VideoFlourishBottom from '../../../assets/images/videoFlourishBottom.svg';
import VideoFlourishTop from '../../../assets/images/videoFlourishTop.svg';
import {
  generateStepText,
  newOnboardingSlideHeight,
  newOnboardingSlideWidth,
  StyledOnboardingFormTitleDiv,
} from './onboardingWelcomeHelpers';
import {OnboardingWelcomeSlide} from './onboardingWelcomeSlide';

/*
 * Props.
 */

interface OnboardingVideoSlideProps {
  onNext: () => void;
  setHasPlayedVideo: (hasPlayedVideo: boolean) => void;
  stepNumber?: number;
  totalStepCount?: number;
  hasPlayedVideo: boolean;
  videoDuration: number;
  setVideoDuration: (d: number) => void;
  nextButtonContent?: ReactNode;
}

/*
 * Style.
 */

const StyledVideo = styled.video`
  display: block;

  width: 888px;
  height: 501px;
`;

const StyledVideoContainerDiv = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  border: 4px solid ${(p) => p.theme.greys.black};
  border-radius: 10px;
  overflow: hidden;

  width: 896px;
  height: 504px;
`;

export const StyledOnboardingVideoSlideContainerDiv = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const StyledOnboardingVideoSlideFlourishContainerDiv = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:before {
    content: '';
    display: inline-block;
    mask-image: url(${VideoFlourishBottom});
    position: absolute;
    width: 39px;
    height: 65px;
    left: -51px;
    background-color: ${(p) => p.theme.greys.black};
  }
  &:after {
    content: '';
    display: inline-block;
    mask-image: url(${VideoFlourishTop});
    position: absolute;
    right: -81px;
    width: 100px;
    height: 104px;
    top: -66px;
    background-color: ${(p) => p.theme.greys.black};
  }
`;

export const StyledOnboardingVideoSlideTitleDiv = styled.div`
  align-self: flex-start;

  margin-bottom: 16px;
  padding-left: 51px;
`;

const StyledPlayButton = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

/*
 * Component.
 */

export const OnboardingVideoSlide: FC<OnboardingVideoSlideProps> = (props) => {
  const {stepNumber, totalStepCount, hasPlayedVideo, videoDuration, setVideoDuration, nextButtonContent} =
    props;
  const shouldRenderSteps = stepNumber && totalStepCount;
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackVideoDuration = () => {
    if (videoRef.current && videoRef.current.currentTime) {
      trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.WATCHED_VIDEO, {
        duration: videoRef.current.currentTime * 1000,
      });
    }
  };

  return (
    <OnboardingWelcomeSlide
      width={newOnboardingSlideWidth}
      height={newOnboardingSlideHeight}
      navSecondary={shouldRenderSteps ? generateStepText(stepNumber, totalStepCount) : undefined}
      navPrimary={
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          size={VisualSizesEnum.MEDIUM}
          onClick={() => {
            if (videoRef.current) {
              setVideoDuration(videoRef.current.currentTime);
            }
            trackVideoDuration();
            props.onNext();
          }}
        >
          {nextButtonContent ?? <Trans id="onboardingwelcome-next">Next</Trans>}
        </Button>
      }
    >
      <StyledOnboardingVideoSlideContainerDiv>
        <StyledOnboardingVideoSlideTitleDiv>
          <StyledOnboardingFormTitleDiv>
            <Trans id="onboardingwelcome-step0-title">Welcome! See what you can do with Front 🎉</Trans>
          </StyledOnboardingFormTitleDiv>
        </StyledOnboardingVideoSlideTitleDiv>
        <StyledOnboardingVideoSlideFlourishContainerDiv>
          <StyledVideoContainerDiv>
            <OnboardingVideo
              ref={videoRef}
              hasPlayedVideo={hasPlayedVideo}
              setHasPlayedVideo={props.setHasPlayedVideo}
              src={
                !hasPlayedVideo
                  ? 'https://dl.frontapp.com/client/front-product-intro-loop.mp4'
                  : 'https://dl.frontapp.com/client/front-product-intro.mp4'
              }
              trackVideoDuration={trackVideoDuration}
              videoDuration={videoDuration}
            />
          </StyledVideoContainerDiv>
        </StyledOnboardingVideoSlideFlourishContainerDiv>
      </StyledOnboardingVideoSlideContainerDiv>
    </OnboardingWelcomeSlide>
  );
};

interface OnboardingVideoProps {
  src: string;
  hasPlayedVideo: boolean;
  setHasPlayedVideo: (played: boolean) => void;
  trackVideoDuration: () => void;
  videoDuration: number;
}

const OnboardingVideo = forwardRef<HTMLVideoElement, OnboardingVideoProps>((props, ref) => {
  const {src, hasPlayedVideo, setHasPlayedVideo, trackVideoDuration, videoDuration} = props;
  const videoRef = useForwardRef<HTMLVideoElement>(ref);

  useEffect(() => {
    videoRef.current.load();
    videoRef.current.currentTime = videoDuration;
  }, [src, videoDuration, videoRef]);

  return (
    <>
      {!hasPlayedVideo && (
        <StyledPlayButton
          src={PlayButton}
          alt="video-play-button"
          onClick={() => {
            setHasPlayedVideo(true);
          }}
        />
      )}
      <StyledVideo
        ref={videoRef}
        autoPlay={videoDuration === 0}
        loop={!hasPlayedVideo}
        playsInline
        muted={!hasPlayedVideo}
        controls={hasPlayedVideo}
        onPlay={() => {
          trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.PLAYED_VIDEO);
        }}
        onClick={() => {
          setHasPlayedVideo(true);
        }}
        onPause={() => {
          trackVideoDuration();
        }}
        onEnded={() => {
          trackVideoDuration();
        }}
      >
        <source src={src} type="video/mp4" />
      </StyledVideo>
    </>
  );
});
